import {
  formatDate,
  formatTime,
  getShortTransactionLogId,
  localDateTimeInputToUtcIso
} from '@smarttransit/common'
import { addParamsToLocation } from '@smarttransit/common-client'
import { addAlert, isAuthorized, isOnlyInvestor as isOnlyInvestorFn } from '../../../utilities/helpers'
import {
  getMessageQueueById,
  searchMessageQueue
} from '../../../services/message-queue-service'
import MessageQueueDetailsDialog from './components/message-queue-details-dialog/template'

const TYPE_OPTIONS = [
  { text: 'SMS', value: 'sms' },
  { text: 'Email', value: 'email' },
  { text: 'Push', value: 'push' }
]

const STATUS_OPTIONS = [
  { text: 'Pending', value: 'pending' },
  { text: 'Processing', value: 'processing' },
  { text: 'Completed', value: 'completed' },
  { text: 'Failed', value: 'failed' }
]

const TYPE_LABELS = {
  sms: 'SMS',
  bulk_sms: 'Bulk SMS',
  email: 'Email',
  push: 'Push'
}

const TYPE_ICONS = {
  sms: 'fas fa-sms',
  bulk_sms: 'fas fa-sms',
  email: 'fas fa-envelope',
  push: 'fas fa-bell'
}

const STATUS_ICONS = {
  pending: { icon: 'fas fa-clock', color: 'orange', label: 'Pending' },
  processing: { icon: 'fas fa-spinner', color: 'blue', label: 'Processing' },
  completed: { icon: 'fas fa-check-circle', color: 'green', label: 'Completed' },
  failed: { icon: 'fas fa-exclamation-circle', color: 'red', label: 'Failed' }
}

const DELIVERY_STATUS_ICONS = {
  pending: { icon: 'fas fa-clock', color: 'orange', label: 'Pending' },
  success: { icon: 'fas fa-check-circle', color: 'green', label: 'Success' },
  failed: { icon: 'fas fa-exclamation-circle', color: 'red', label: 'Failed' },
  unknown: { icon: 'fas fa-question-circle', color: 'grey', label: 'Unknown' }
}

function formatDateTimeLabel (date) {
  if (!date) {
    return '—'
  }
  return formatDate(date) + ' - ' + formatTime(date)
}

function queryList (value) {
  if (!value) {
    return []
  }
  return (Array.isArray(value) ? value : [value]).filter(Boolean)
}

function mapMessageQueueForDisplay (record) {
  const metadata = record && record.metadata ? record.metadata : {}
  const recipients = Array.isArray(metadata.recipients) ? metadata.recipients : []
  const isBulk = record.messageType === 'bulk_sms'
  const latestStatus = metadata.latestDeliveryStatus
  const smsGhana = metadata.smsGhana || {}
  const deliveryReports = metadata.deliveryReports || {}
  const deliveryReportRows = Object.keys(deliveryReports).map(function (id) {
    const report = deliveryReports[id] || {}
    const status = report.status || {}
    return {
      toLabel: report.to || id,
      statusLabel: status.label || status.id || '—'
    }
  })

  let providerStatusLabel = '—'
  if (latestStatus && (latestStatus.label || latestStatus.id)) {
    providerStatusLabel = latestStatus.label || String(latestStatus.id)
  } else if (smsGhana.destinations && smsGhana.destinations.length === 1 && smsGhana.destinations[0].status) {
    providerStatusLabel = smsGhana.destinations[0].status.label || String(smsGhana.destinations[0].status.id)
  } else if (deliveryReportRows.length) {
    providerStatusLabel = deliveryReportRows.length + ' report(s)'
  }

  const deliveryStatus = record.deliveryStatus || metadata.deliveryStatus || 'unknown'
  const deliveryStatusIconData = DELIVERY_STATUS_ICONS[deliveryStatus] || DELIVERY_STATUS_ICONS.unknown

  return Object.assign({}, record, {
    typeLabel: TYPE_LABELS[record.messageType] || record.messageType || '—',
    typeIcon: TYPE_ICONS[record.messageType] || 'fas fa-comment',
    statusIconData: STATUS_ICONS[record.messageStatus] || STATUS_ICONS.pending,
    statusLabel: (STATUS_ICONS[record.messageStatus] && STATUS_ICONS[record.messageStatus].label) || record.messageStatus || '—',
    recipientLabel: isBulk
      ? (recipients.length + ' recipient' + (recipients.length === 1 ? '' : 's'))
      : (record.recipient || '—'),
    retryLabel: String(record.retryCount || 0) + ' / ' + String(record.maxRetries || 0),
    contentLabel: record.messageContent || '—',
    deliveryStatus: deliveryStatus,
    deliveryStatusIconData: deliveryStatusIconData,
    deliveryStatusLabel: deliveryStatusIconData.label,
    providerStatusLabel: providerStatusLabel,
    dateCreatedLabel: formatDateTimeLabel(record.dateCreated),
    dateUpdatedLabel: formatDateTimeLabel(record.dateUpdated),
    idLabel: record.id ? getShortTransactionLogId(record.id) : '',
    deliveryReportRows: deliveryReportRows
  })
}

export default {
  name: 'advanced-message-queue',
  components: {
    MessageQueueDetailsDialog
  },
  props: {
    signedInUser: Object
  },
  data () {
    return {
      apiInProgress: false,
      searchRecipient: '',
      selectedStatuses: [],
      selectedTypes: [],
      dateFrom: null,
      dateTo: null,
      typeOptions: TYPE_OPTIONS,
      statusOptions: STATUS_OPTIONS,
      messages: [],
      totalMessages: 0,
      detailsDialogOpen: false,
      selectedMessage: null,
      lastParams: {},
      componentMounted: false,
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 10,
        sortBy: 'dateUpdated'
      },
      headers: [
        { text: 'Type', align: 'left', sortable: true, value: 'messageType' },
        { text: 'Recipient', align: 'left', sortable: true, value: 'recipient' },
        { text: 'Content', align: 'left', sortable: false },
        { text: 'Status', align: 'left', sortable: true, value: 'messageStatus' },
        { text: 'Delivery', align: 'left', sortable: false },
        { text: 'Retries', align: 'left', sortable: true, value: 'retryCount' },
        { text: 'Created', align: 'left', sortable: true, value: 'dateCreated' },
        { text: 'Updated', align: 'left', sortable: true, value: 'dateUpdated' }
      ]
    }
  },
  computed: {
    isAdmin () {
      return isAuthorized(this.$props.signedInUser, 'admin')
    },
    isOnlyInvestor () {
      return isOnlyInvestorFn(this.$props.signedInUser)
    }
  },
  watch: {
    pagination (updatedPagination) {
      this.lastParams = addParamsToLocation(updatedPagination, this.$route, this.lastParams)
    }
  },
  mounted () {
    this.loadFromQueryParams()
    this.searchMessages().then(() => {
      this.componentMounted = true
    })
  },
  methods: {
    loadFromQueryParams () {
      if (this.$route.query.descending) {
        this.pagination.descending = this.$route.query.descending === 'true' || this.$route.query.descending === true
      }
      if (this.$route.query.page) {
        this.pagination.page = Number(this.$route.query.page) || 1
      }
      if (this.$route.query.rowsPerPage) {
        this.pagination.rowsPerPage = Number(this.$route.query.rowsPerPage) || 10
      }
      if (this.$route.query.sortBy) {
        this.pagination.sortBy = this.$route.query.sortBy
      }
      if (this.$route.query.recipient) {
        this.searchRecipient = this.$route.query.recipient
      }
      if (this.$route.query.dateFrom) {
        this.dateFrom = this.$route.query.dateFrom
      }
      if (this.$route.query.dateTo) {
        this.dateTo = this.$route.query.dateTo
      }
      this.selectedStatuses = queryList(this.$route.query.messageStatus)
      this.selectedTypes = queryList(this.$route.query.messageType)
    },
    submitSearch () {
      this.lastParams = addParamsToLocation({
        recipient: this.searchRecipient,
        messageStatus: this.selectedStatuses,
        messageType: this.selectedTypes,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo
      }, this.$route, this.lastParams)
      this.pagination.page = 1
      if (this.componentMounted) {
        this.searchMessages()
      }
    },
    buildSearchOptions () {
      const { sortBy, descending, page, rowsPerPage } = this.pagination
      return {
        recipient: this.searchRecipient,
        messageStatus: this.selectedStatuses,
        messageType: this.selectedTypes,
        dateFrom: localDateTimeInputToUtcIso(this.dateFrom),
        dateTo: localDateTimeInputToUtcIso(this.dateTo),
        orderBy: sortBy || 'dateUpdated',
        sortOrder: descending ? 'DESC' : 'ASC',
        page: page,
        limit: rowsPerPage
      }
    },
    async searchMessages () {
      if (!this.isAdmin || this.isOnlyInvestor) {
        return
      }
      this.apiInProgress = true
      try {
        const result = await searchMessageQueue(this.buildSearchOptions())
        const messages = result && result.messages ? result.messages : []
        this.messages = messages.map(mapMessageQueueForDisplay)
        this.totalMessages = result && typeof result.count === 'number' ? result.count : messages.length
      } catch (error) {
        addAlert({ message: error, type: 'error' })
      } finally {
        this.apiInProgress = false
      }
    },
    onPagination () {
      if (this.componentMounted) {
        this.searchMessages()
      }
    },
    async openDetails (item) {
      this.selectedMessage = item
      this.detailsDialogOpen = true
      try {
        const fresh = await getMessageQueueById(item.id)
        this.selectedMessage = mapMessageQueueForDisplay(fresh)
      } catch (error) {
        addAlert({ message: error, type: 'error' })
      }
    }
  }
}
